import { DatePicker } from "@leafygreen-ui/date-picker";
import {
  DateType,
  isInvalidDateObject,
  setToUTCMidnight,
} from "@leafygreen-ui/date-utils";
import { Description, Label } from "@leafygreen-ui/typography";
import { labelValue } from "@rjsf/utils";
import { fromZonedTime, toZonedTime } from "date-fns-tz";
import LGTimePicker from "components/TimePicker";
import { useUserTimeZone } from "hooks/useUserTimeZone";
import ElementWrapper from "../ElementWrapper";
import styles from "./DateTimePicker.module.css";
import { SpruceWidgetProps } from "./types";

enum Caller {
  Date,
  Time,
}

export const DateTimePicker: React.FC<
  {
    options: {
      disableBefore?: Date;
      disableAfter?: Date;
    };
  } & SpruceWidgetProps
> = ({
  disabled,
  hideLabel,
  id,
  label,
  onChange,
  options,
  readonly,
  value = "",
}) => {
  const isDisabled = disabled === true || readonly === true;
  const {
    description,
    disableAfter,
    disableBefore,
    elementWrapperCSS,
    showLabel,
  } = options;
  const shouldShowLabel = showLabel ?? !hideLabel;
  const widgetLabel = labelValue(label, !shouldShowLabel);

  const timezone = useUserTimeZone();

  const currentDateTime = timezone
    ? toZonedTime(new Date(value || null), timezone)
    : new Date(value || null);

  const handleChange = (caller: Caller) => (newDate?: DateType) => {
    if (!newDate) return;
    if (isInvalidDateObject(newDate)) return;

    // LG DatePicker overwrites the time, so preserve it as such
    if (caller === Caller.Date) {
      const hours = currentDateTime.getUTCHours();
      const minutes = currentDateTime.getUTCMinutes();
      newDate.setUTCHours(hours);
      newDate.setUTCMinutes(minutes);
    }

    if (timezone) {
      onChange(fromZonedTime(newDate, timezone).toString());
    } else {
      onChange(newDate.toString());
    }
  };

  return (
    <ElementWrapper css={elementWrapperCSS}>
      {shouldShowLabel && (
        <Label disabled={isDisabled} htmlFor={id}>
          {widgetLabel}
        </Label>
      )}
      {description && <Description>{description}</Description>}
      <div className={styles.dateTimeContainer}>
        <DatePicker
          aria-label="date-picker"
          data-testid="date-picker"
          disabled={isDisabled}
          max={disableAfter ? setToUTCMidnight(disableAfter) : undefined}
          min={disableBefore ? setToUTCMidnight(disableBefore) : undefined}
          onDateChange={handleChange(Caller.Date)}
          value={currentDateTime}
        />
        {/* TODO: Replace with official component following completion of LG-3931.
         * Additionally, uninstall @leafygreen-ui/form-field. */}
        <LGTimePicker
          data-testid="time-picker"
          disabled={isDisabled}
          onDateChange={handleChange(Caller.Time)}
          value={currentDateTime}
        />
      </div>
    </ElementWrapper>
  );
};

export const TimePicker: React.FC<SpruceWidgetProps> = ({
  disabled,
  hideLabel,
  label,
  onChange,
  options,
  readonly,
  value,
}) => {
  const { description, elementWrapperCSS } = options;
  const isDisabled = disabled === true || readonly === true;
  const widgetLabel = labelValue(label, hideLabel);
  const currentDateTime = new Date(value || null);

  const handleChange = (d?: DateType) => {
    if (d) {
      onChange(d.toString());
    }
  };

  return (
    <ElementWrapper css={elementWrapperCSS}>
      {description && <Description>{description}</Description>}
      {/* TODO: Replace with official component following completion of LG-3931.
       * Additionally, uninstall @leafygreen-ui/form-field. */}
      <LGTimePicker
        data-testid="time-picker"
        disabled={isDisabled}
        label={widgetLabel}
        onDateChange={handleChange}
        value={currentDateTime}
      />
    </ElementWrapper>
  );
};
